import { useState, useEffect, useCallback } from "react";
import { BrandMark } from "@/components/BrandMark";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import { Pulse, Flame, Trophy, SignOut, PlusCircle, House, ChartLineUp, ChatCircleDots, UserCircle, BookOpen, ClockCounterClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HealthspanRing from "@/components/dashboard/HealthspanRing";
import Pillars from "@/components/dashboard/Pillars";
import DailyPlan from "@/components/dashboard/DailyPlan";
import NutritionCard from "@/components/dashboard/NutritionCard";
import TwinInsights from "@/components/dashboard/TwinInsights";
import CoachChat from "@/components/dashboard/CoachChat";
import CheckinDialog from "@/components/dashboard/CheckinDialog";
import Nudges from "@/components/dashboard/Nudges";
import NotificationBell from "@/components/dashboard/NotificationBell";
import ThemePicker from "@/components/dashboard/ThemePicker";
import ProfileTab from "@/components/dashboard/ProfileTab";
import GuidedTour from "@/components/dashboard/GuidedTour";
import InstallPrompt from "@/components/InstallPrompt";
import HistoryTab from "@/components/dashboard/HistoryTab";
import FeedbackPrompt from "@/components/dashboard/FeedbackPrompt";
import BreathingTool from "@/components/dashboard/BreathingTool";
import { ProLock, trialDaysLeft } from "@/components/ProGate";
import { toast } from "sonner";

const TABS = [
  { id: "today", label: "Today", icon: House },
  { id: "healthspan", label: "Healthspan", icon: ChartLineUp },
  { id: "history", label: "History", icon: ClockCounterClockwise },
  { id: "coach", label: "Coach", icon: ChatCircleDots },
  { id: "profile", label: "Profile", icon: UserCircle },
];

export default function Dashboard() {
  const { user, logout, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((x) => x.id === t) ? t : "today";
  });
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [breatheOpen, setBreatheOpen] = useState(false);

  const handleNudgeAction = (action) => {
    if (action === "checkin") setCheckinOpen(true);
    else if (action === "coach") setTab("coach");
    else if (action === "profile") setTab("profile");
    else if (action === "breathe") setBreatheOpen(true);
  };
  const updateNudges = (next) => setData((d) => (d ? { ...d, nudges: next } : d));

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const checkout = params.get("checkout");
    if (checkout === "cancel") {
      window.history.replaceState({}, "", "/app");
      toast.info("Checkout cancelled — Pro is here whenever you're ready.");
      return;
    }
    if (!sid) return;
    window.history.replaceState({}, "", "/app");
    let tries = 0;
    const poll = async () => {
      if (tries++ > 10) {
        toast.info("Payment is processing — Pro will unlock as soon as it confirms.");
        return;
      }
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Welcome to Ikavio Pro 🎉 Everything is unlocked.", { duration: 10000 });
          await checkAuth();
          load();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          toast.error("Payment didn't complete — you weren't charged.");
          return;
        }
      } catch {
        // keep polling
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [checkAuth, load]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="w-10 h-10 rounded-full border-2 border-brand border-t-transparent animate-spin" />
      </div>
    );
  }

  const gen = data.generated;
  const firstName = (user?.name || "there").split(" ")[0];
  const beginner = data.profile?.experience_level === "beginner";
  const ent = user?.entitlement || { is_pro: true };
  const trialDays = trialDaysLeft(ent);

  return (
    <div className="min-h-screen bg-bg">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-bg/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark size={32} />
            <span className="font-display text-lg font-semibold tracking-tight hidden sm:block">Ikavio</span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Stat icon={Flame} color="var(--brand)" value={data.streak} label="day streak" />
            <Stat icon={Trophy} color="#E8A54B" value={`Lv ${data.level}`} label="level" />
            <ThemePicker />
            <Link
              to="/glossary"
              data-testid="glossary-link-btn"
              title="Glossary — what do these terms mean?"
              className="w-9 h-9 rounded-full bg-surface border border-border flex items-center justify-center hover:bg-secondary transition-colors"
            >
              <BookOpen size={17} weight="fill" color="var(--brand)" />
            </Link>
            <NotificationBell />
            <div className="flex items-center gap-1.5 bg-surface border border-border rounded-full pl-3 pr-1.5 py-1.5">
              <span className="text-xs text-muted2 hidden sm:inline">HS</span>
              <span className="font-display font-semibold text-brand tabular-nums">{data.score}</span>
              <button onClick={logout} data-testid="logout-btn" title="Sign out"
                className="w-7 h-7 rounded-full bg-secondary hover:bg-border flex items-center justify-center ml-1">
                <SignOut size={15} weight="bold" color="#7C8AA5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs (desktop/tablet) */}
        <div className="max-w-6xl mx-auto px-5 hidden sm:flex gap-1 overflow-x-auto scrollbar-none">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap ${
                tab === t.id ? "border-brand text-brand" : "border-transparent text-muted2 hover:text-ink"}`}>
              <t.icon size={16} weight={tab === t.id ? "fill" : "regular"} />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 pt-8 pb-28 sm:pb-8">
        {/* Greeting */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`${tab === "coach" ? "hidden sm:flex" : "flex"} mb-6 items-end justify-between flex-wrap gap-3`}>
          <div>
            <span className="label-eyebrow">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
            <h1 className="font-display font-light tracking-tighter text-3xl sm:text-4xl mt-1">
              {greetTime()}, {firstName}
            </h1>
          </div>
          <button data-testid="open-checkin-btn" onClick={() => setCheckinOpen(true)}
            className="inline-flex items-center gap-2 bg-ink text-bg rounded-full px-5 py-3 font-medium hover:bg-brand transition-colors">
            <PlusCircle size={18} weight="fill" />
            {data.needs_checkin ? "Daily check-in" : "Update today"}
          </button>
        </motion.div>

        {trialDays && (
          <button
            data-testid="trial-chip"
            onClick={() => setTab("profile")}
            className={`${tab === "coach" ? "hidden sm:inline-flex" : "inline-flex"} items-center gap-2 mb-4 text-xs font-medium bg-brand/10 text-brand rounded-full px-3.5 py-2 hover:bg-brand/20 transition-colors`}
          >
            Pro trial — {trialDays} day{trialDays === 1 ? "" : "s"} left · see plans →
          </button>
        )}

        {data.needs_checkin && (
          <div className={`${tab === "coach" ? "hidden sm:flex" : "flex"} clay p-6 mb-6 items-center gap-4 border-brand/30 bg-brand/5`} data-testid="checkin-banner">
            <div className="w-11 h-11 rounded-2xl bg-brand/15 flex items-center justify-center shrink-0">
              <Pulse size={22} weight="fill" color="var(--brand)" />
            </div>
            <div className="flex-1">
              <p className="font-medium">Ready to plan your optimal day?</p>
              <p className="text-sm text-muted2">Do a 30-second check-in and Ikavio will adapt everything to how you feel today.</p>
            </div>
          </div>
        )}

        {data.mode === "mvd" && <MvdBanner tab={tab} onNormal={async () => { await api.post("/mode/normal"); load(); }} />}

        <div className={tab === "coach" ? "hidden sm:block" : "block"}>
          {data.feedback_prompt && (
            <FeedbackPrompt prompt={data.feedback_prompt}
              onDone={() => setData((d) => ({ ...d, feedback_prompt: null }))} />
          )}
          <Nudges nudges={data.nudges} onAction={handleNudgeAction} onChange={updateNudges} />
        </div>

        {tab === "today" && (
          <div className="grid lg:grid-cols-3 gap-6 fade-up">
            <div className="lg:col-span-1"><HealthspanRing score={data.score} biologicalAge={data.twin?.biological_age} chronoAge={data.twin?.chronological_age} longevity={data.twin?.longevity} beginner={beginner} recoverySmart={data.recovery_smart} /></div>
            <div className="lg:col-span-2 space-y-6">
              {gen ? <DailyPlan plan={gen.plan} /> : <EmptyPlan onCheckin={() => setCheckinOpen(true)} />}
              <div className="grid md:grid-cols-2 gap-6">
                {gen && (ent.is_pro
                  ? <NutritionCard nutrition={gen.nutrition} />
                  : <ProLock testId="nutrition-locked" feature="Adaptive Nutrition" desc="Meal guidance that flexes with your day. Your 7-day Pro trial has ended." />)}
                {ent.is_pro
                  ? <TwinInsights twin={data.twin} insight={gen?.insight} />
                  : <ProLock testId="twin-locked" feature="Digital Twin" desc="Baselines, anomaly alerts and trajectory predictions. Your 7-day Pro trial has ended." />}
              </div>
            </div>
          </div>
        )}

        {tab === "healthspan" && (
          <div className="grid lg:grid-cols-3 gap-6 fade-up">
            <div className="lg:col-span-1 space-y-6">
              <HealthspanRing score={data.score} biologicalAge={data.twin?.biological_age} chronoAge={data.twin?.chronological_age} longevity={data.twin?.longevity} beginner={beginner} recoverySmart={data.recovery_smart} />
            </div>
            <div className="lg:col-span-2 space-y-6">
              <Pillars pillars={data.pillars} beginner={beginner} />
              <div className="clay p-6" data-testid="trend-card">
                <span className="label-eyebrow">Healthspan trend</span>
                <div className="h-56 mt-4">
                  {data.trend?.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.trend} margin={{ left: -20, right: 8, top: 8 }}>
                        <defs>
                          <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#7C8AA5" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 1000]} tick={{ fontSize: 12, fill: "#7C8AA5" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ borderRadius: 16, border: "1px solid #E7E5DF" }} />
                        <Area type="monotone" dataKey="score" stroke="var(--brand)" strokeWidth={2.5} fill="url(#area)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-muted2">
                      Check in daily to build your Healthspan trend.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "history" && (
          <div className="max-w-3xl mx-auto fade-up"><HistoryTab /></div>
        )}

        {tab === "coach" && (
          <div className="max-w-3xl mx-auto fade-up">
            {ent.is_pro
              ? <CoachChat />
              : <ProLock testId="coach-locked" feature="Ikavio Coach" desc="Unlimited AI coaching that knows your data, memory and goals. Your 7-day Pro trial has ended." />}
          </div>
        )}

        {tab === "profile" && (
          <div className="max-w-3xl mx-auto fade-up"><ProfileTab profile={data.profile} onReload={load} /></div>
        )}
      </main>

      <CheckinDialog open={checkinOpen} onOpenChange={setCheckinOpen} onDone={load} />
      <BreathingTool open={breatheOpen} onOpenChange={setBreatheOpen} />
      <GuidedTour userId={user?.user_id} />
      <InstallPrompt />

      {/* Bottom nav (mobile) */}
      <nav
        data-testid="bottom-nav"
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-surface/95 backdrop-blur-xl border-t border-border pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`bottom-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex flex-col items-center gap-0.5 pt-2.5 pb-2 text-[10px] font-medium transition-colors ${
                tab === t.id ? "text-brand" : "text-muted2"
              }`}
            >
              <t.icon size={22} weight={tab === t.id ? "fill" : "regular"} />
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  return (
    <div className="hidden sm:flex items-center gap-2 bg-surface border border-border rounded-full px-3 py-1.5">
      <Icon size={16} weight="fill" color={color} />
      <span className="font-semibold text-sm tabular-nums">{value}</span>
      <span className="text-xs text-muted2">{label}</span>
    </div>
  );
}

function MvdBanner({ tab, onNormal }) {
  return (
    <div className={`${tab === "coach" ? "hidden sm:flex" : "flex"} clay p-5 mb-6 items-center gap-4 flex-wrap`}
      style={{ background: "rgba(22,188,156,0.08)", borderColor: "rgba(22,188,156,0.3)" }}
      data-testid="mvd-banner">
      <div className="w-11 h-11 rounded-2xl bg-sage/15 flex items-center justify-center shrink-0">
        <Pulse size={22} weight="fill" color="#16BC9C" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className="font-medium">Pressure off today</p>
        <p className="text-sm text-muted2 mt-0.5">
          The last few days have been heavy, so I've made today deliberately tiny. No metrics, no targets — just the basics.
        </p>
      </div>
      <button data-testid="mvd-normal-btn" onClick={onNormal}
        className="rounded-full bg-surface border border-border px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors shrink-0">
        I'm good — give me my normal day
      </button>
    </div>
  );
}

function EmptyPlan({ onCheckin }) {
  return (
    <div className="clay p-10 text-center" data-testid="empty-plan">
      <p className="font-display text-xl font-light">No plan yet for today</p>
      <p className="text-sm text-muted2 mt-2 max-w-sm mx-auto">Check in and Ikavio will design your single most optimal day — workout, recovery and nutrition, adapted to you.</p>
      <button onClick={onCheckin} data-testid="empty-plan-checkin-btn" className="mt-5 bg-brand text-white rounded-full px-6 py-3 font-medium hover:bg-ink transition-colors">
        Start check-in
      </button>
    </div>
  );
}

function greetTime() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
